import type { UploaderFile, UploaderMeta } from '../types';
import type { parseFileUploadRestrictions } from './parseFileUploadRestrictions';
import type { Meta, UploadResult } from '@uppy/core';
import type Uppy from '@uppy/core';

import { useEffect } from 'react';

import { useLatest } from './useLatest';

type AnyUploadResult = UploadResult<Meta, Record<string, never>>;

type UploaderEventOptions<TMeta extends UploaderMeta> = {
  clearConfirmationError: () => void;
  formatRestrictionError: (
    error: Error,
    allowedFileTypes: string[] | undefined,
  ) => string;
  onError?: (fileName: string, error: Error) => void;
  onFileAdded?: (file: UploaderFile<TMeta>) => void;
  onStartUpload?: (uploadId: string, files: UploaderFile<TMeta>[]) => void;
  onUploadComplete?: (result: AnyUploadResult) => void;
  onUploadProgress?: (
    file: UploaderFile<TMeta> | undefined,
    progress: { bytesUploaded: number; bytesTotal: number | null },
  ) => void;
  setRestrictionError: (error: string | null) => void;
  validatedRestrictions:
    | ReturnType<typeof parseFileUploadRestrictions>
    | undefined;
  uppy: Uppy<Meta, Record<string, never>>;
};

/**
 * Subscribes once to the Uppy events the uploader reacts to and forwards them
 * to the latest consumer callbacks, so callers can pass inline functions
 * without resubscribing.
 *
 * Adding a file clears both the restriction and the confirmation error; a
 * restriction failure sets the restriction error with the allowed types made
 * readable; upload errors reach `onError` as an `Error` with the file name.
 */
export function useUploaderEvents<TMeta extends UploaderMeta>({
  clearConfirmationError,
  formatRestrictionError,
  onError,
  onFileAdded,
  onStartUpload,
  onUploadComplete,
  onUploadProgress,
  setRestrictionError,
  validatedRestrictions,
  uppy,
}: UploaderEventOptions<TMeta>) {
  const latest = useLatest({
    allowedFileTypes: validatedRestrictions?.allowedFileTypes,
    clearConfirmationError,
    formatRestrictionError,
    onError,
    onFileAdded,
    onStartUpload,
    onUploadComplete,
    onUploadProgress,
    setRestrictionError,
  });

  useEffect(() => {
    const onFileAddedHandler = (file: UploaderFile) => {
      const { clearConfirmationError, onFileAdded, setRestrictionError } =
        latest.current;
      clearConfirmationError();
      setRestrictionError(null);
      onFileAdded?.(file as UploaderFile<TMeta>);
    };

    const onUploadHandler = (uploadId: string, files: UploaderFile[]) => {
      latest.current.onStartUpload?.(uploadId, files as UploaderFile<TMeta>[]);
    };

    const onUploadProgressHandler = (
      file: UploaderFile | undefined,
      progress: { bytesUploaded: number; bytesTotal: number | null },
    ) => {
      latest.current.onUploadProgress?.(
        file as UploaderFile<TMeta> | undefined,
        progress,
      );
    };

    const onUploadCompleteHandler = (result: AnyUploadResult) => {
      latest.current.onUploadComplete?.(result);
    };

    const onRestrictionFailedHandler = (
      _file: UploaderFile | undefined,
      error: Error,
    ) => {
      const { allowedFileTypes, formatRestrictionError, setRestrictionError } =
        latest.current;
      setRestrictionError(formatRestrictionError(error, allowedFileTypes));
    };

    const onUploadErrorHandler = (
      file: UploaderFile | undefined,
      error: unknown,
    ) => {
      latest.current.onError?.(
        file?.name ?? '',
        error instanceof Error ? error : new Error(String(error)),
      );
    };

    uppy.on('file-added', onFileAddedHandler);
    uppy.on('upload', onUploadHandler);
    uppy.on('upload-progress', onUploadProgressHandler);
    uppy.on('complete', onUploadCompleteHandler);
    uppy.on('restriction-failed', onRestrictionFailedHandler);
    uppy.on('upload-error', onUploadErrorHandler);

    return () => {
      uppy.off('file-added', onFileAddedHandler);
      uppy.off('upload', onUploadHandler);
      uppy.off('upload-progress', onUploadProgressHandler);
      uppy.off('complete', onUploadCompleteHandler);
      uppy.off('restriction-failed', onRestrictionFailedHandler);
      uppy.off('upload-error', onUploadErrorHandler);
    };
  }, [latest, uppy]);
}
